// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { access } from "./messages/access.js";
import { adminConsole } from "./messages/adminConsole.js";
import { adminAssistant } from "./messages/adminAssistant.js";
import { adminSupport } from "./messages/adminSupport.js";
import { billing } from "./messages/billing.js";
import { drive } from "./messages/drive.js";
import { editorTools } from "./messages/editorTools.js";
import { integrations } from "./messages/integrations.js";
import { notices } from "./messages/notices.js";
import { panels } from "./messages/panels.js";
import { settings } from "./messages/settings.js";
import { shopify } from "./messages/shopify.js";
import { vaultUi } from "./messages/vaultUi.js";
import { workspaceAdmin } from "./messages/workspaceAdmin.js";

/**
 * English UI strings: the source catalog, with each area's strings in messages/ (keys are
 * prefixed by area, so they never collide). Other locales translate these keys and fall back
 * to English for anything missing. Placeholders look like {name}.
 */
export const en = {
  "app.name": "Granite",

  "common.email": "Email",
  "common.password": "Password",
  "common.backToSignIn": "Back to sign in",
  "common.signIn": "Sign in",
  "common.loading": "Loading…",

  "login.title": "Sign in to Granite",
  "login.submit": "Sign in",
  "login.forgot": "Forgot your password?",
  "login.or": "or",
  "login.continueGoogle": "Continue with Google",
  "login.continueApple": "Continue with Apple",
  "login.continueSSO": "Continue with SSO",
  "appSignIn.title": "Sign in to the Granite app",
  "appSignIn.confirm": "Continue in the Granite app as {email}?",
  "appSignIn.continue": "Open Granite",
  "appSignIn.otherAccount": "Use another account",
  "appSignIn.redirecting": "Taking you to sign in…",
  "appSignIn.invalid": "This sign-in link is not complete. Start again from the Granite app.",
  "appSignIn.doneTitle": "You're signed in",
  "appSignIn.done": "Granite is opening. You can close this tab.",
  "appSignIn.openAgain": "Open Granite again",
  "login.finishInBrowser": "Finish signing in in your browser. Granite opens again when you're done.",
  "google.title": "Continue with Google",
  "google.working": "Signing you in…",
  "google.canceled": "Signing in with Google was canceled.",
  "google.invalid": "This Google sign-in has expired or was started in another browser. Try again.",
  "google.back": "Back to sign in",
  "apple.title": "Continue with Apple",
  "apple.working": "Signing you in…",
  "apple.canceled": "Signing in with Apple was canceled.",
  "apple.invalid": "This Apple sign-in has expired or was started in another browser. Try again.",
  "apple.back": "Back to sign in",
  "login.noAccount": "New to Granite?",
  "login.createAccount": "Create an account",
  "login.resendVerification": "Send a new confirmation email",

  "mfa.title": "Two-step verification",
  "mfa.intro": "Enter the 6-digit code from your authenticator app.",
  "mfa.introRecovery": "Enter one of the recovery codes you saved when you turned on two-step verification.",
  "mfa.code": "Authentication code",
  "mfa.recoveryCode": "Recovery code",
  "mfa.submit": "Verify",
  "mfa.useRecovery": "Use a recovery code instead",
  "mfa.useApp": "Use your authenticator app instead",
  "mfa.startOver": "Start over",

  "password.strength.label": "Password strength:",
  "password.strength.0": "Too easy to guess",
  "password.strength.1": "Weak",
  "password.strength.2": "Fair",
  "password.strength.3": "Good",
  "password.strength.4": "Strong",
  "password.tooWeak":
    "This password is too easy to guess. Make it longer, or use a few unrelated words, and avoid names, dates, and keyboard patterns.",

  "signup.title": "Create your Granite account",
  "signup.name": "Name",
  "signup.nameHint": "Optional. Shown to people you share notes with.",
  "signup.passwordHint": "At least 10 characters, and hard to guess.",
  "signup.submit": "Create account",
  "signup.agreeBefore": "By creating an account, you agree to the",
  "signup.agreeAnd": "and the",
  "signup.terms": "Terms of Service",
  "signup.privacy": "Privacy Policy",
  "signup.haveAccount": "Already have an account?",
  "signup.businessTitle": "Create your business account",
  "signup.kind": "Account type",
  "signup.kind.personal": "Personal",
  "signup.kind.business": "Business",
  "signup.company": "Company name",
  "signup.companyHint": "Your workspace. You own it and invite your team; 30 days free.",

  "checkEmail.title": "Check your email",
  "checkEmail.sentTo": "We sent a confirmation link to {email}. Open it to finish creating your account.",
  "checkEmail.generic": "Enter your email and we will send a new confirmation link.",
  "checkEmail.resend": "Send the link again",
  "checkEmail.resent": "If that address has an unconfirmed account, a new link is on its way.",

  "verify.title": "Confirm your email",
  "verify.pending": "Confirming your email…",
  "verify.done": "Your email is confirmed. You can sign in now.",
  "verify.missingToken": "This link is incomplete. Open the link from the email again, or request a new one.",
  "verify.requestNew": "Send a new link",

  "reset.title": "Reset your password",
  "reset.intro": "Enter your account email and we will send you a link to set a new password.",
  "reset.submit": "Send reset link",
  "reset.sent": "If an account exists for {email}, a reset link is on its way. It works for one hour.",
  "reset.newTitle": "Set a new password",
  "reset.newPassword": "New password",
  "reset.confirmPassword": "Repeat the new password",
  "reset.mismatch": "The passwords do not match.",
  "reset.save": "Save password",
  "reset.done": "Your password is changed and every device was signed out. Sign in with the new password.",

  "home.title": "Your vaults",
  "home.signedInAs": "Signed in as {email}",
  "home.signOut": "Sign out",
  "home.empty": "You have no vaults yet. Vaults you create on any device appear here.",
  "home.updated": "Updated {date}",
  "home.createTitle": "Create a vault",
  "home.vaultName": "New vault name",
  "home.createVault": "Create vault",
  "home.offline": "You are offline. Vaults opened on this device before still work; changes sync when you are back online.",

  "notFound.title": "Page not found",
  "notFound.body": "There is nothing at this address.",
  "notFound.home": "Go to Granite",

  "crash.title": "Something went wrong",
  "crash.body": "Granite hit an unexpected error. Reload the page to try again.",
  "crash.reload": "Reload",

  "error.network": "Granite could not reach the server. Check your connection and try again.",
  "error.unexpected": "Something went wrong. Please try again.",
  "error.rate_limited": "Too many attempts. Wait a minute and try again.",
  "error.plan_limit_knowledge_base": "Semantic search needs the Pro or Business plan.",
  "error.kb_disabled": "Semantic search is turned off right now. Try again later.",
  "error.kb_rate_limited": "Too many searches. Wait a minute and try again.",
  "error.invalid_credentials": "The email or password is incorrect.",
  "error.email_not_verified": "Confirm your email address before signing in.",
  "error.account_disabled": "This account is disabled.",
  "error.invalid_token": "This link is invalid or has expired.",
  "error.invalid_code": "The code is incorrect.",
  "error.invalid_mfa_token": "The sign-in attempt expired. Start again.",
  "error.refresh_token_reused": "You were signed out for your security. Sign in again.",
  ...workspaceAdmin,
  ...adminConsole,
  ...adminSupport,
  ...adminAssistant,
  ...settings,
  ...billing,
  ...drive,
  ...integrations,
  ...vaultUi,
  ...panels,
  ...access,
  ...editorTools,
  ...notices,
  ...shopify,
} as const;

export type MessageKey = keyof typeof en;
